/**
 * Bộ chart SVG nhẹ cho admin (không thêm thư viện).
 * Quy ước: line 2px, cột ≤ 24px bo 4px phía trên, lưới hairline, màu theo thứ tự cố định
 * (--viz-1 → --viz-3, đã kiểm tra mù màu), chữ luôn dùng màu text — không dùng màu series.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus, Table2 } from "lucide-react";
import { cx } from "../../pages/admin/_shared";

export const VIZ = ["var(--viz-1)", "var(--viz-2)", "var(--viz-3)"];

/** Bảng màu nhấn cho thẻ (mỗi thẻ một màu nhận diện) — chip icon, dải màu trên cùng, sparkline. */
export const TONES = {
  blue: { chip: "bg-blue-100 text-blue-700", bar: "from-blue-500 to-sky-400", glow: "from-blue-50", hex: "#2563eb" },
  violet: { chip: "bg-violet-100 text-violet-700", bar: "from-violet-500 to-fuchsia-400", glow: "from-violet-50", hex: "#7c3aed" },
  amber: { chip: "bg-amber-100 text-amber-700", bar: "from-amber-500 to-orange-400", glow: "from-amber-50", hex: "#d97706" },
  rose: { chip: "bg-rose-100 text-rose-700", bar: "from-rose-500 to-pink-400", glow: "from-rose-50", hex: "#e11d48" },
  emerald: { chip: "bg-emerald-100 text-emerald-700", bar: "from-emerald-500 to-teal-400", glow: "from-emerald-50", hex: "#059669" },
  cyan: { chip: "bg-cyan-100 text-cyan-700", bar: "from-cyan-500 to-sky-400", glow: "from-cyan-50", hex: "#0891b2" },
  slate: { chip: "bg-slate-100 text-slate-700", bar: "from-slate-500 to-slate-400", glow: "from-slate-50", hex: "#475569" },
};

const nf = new Intl.NumberFormat("vi-VN");
export const fmtNum = (n) => nf.format(Math.round(Number(n) || 0));

export function fmtCompact(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return String(Math.round(v));
}

/** Bước trục "tròn" (1, 2, 5 × 10^n) để tick dễ đọc. */
export function niceScale(max, count = 4) {
  const m = Math.max(1, Number(max) || 0);
  const raw = m / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((k) => k * pow).find((s) => s >= raw) || pow * 10;
  const top = Math.ceil(m / step) * step;
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
  return { top, ticks };
}

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ─── Legend ──────────────────────────────────────────────────────────────────
export const Legend = ({ items, shape = "line" }) => (
  <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
    {items.map((it) => (
      <li key={it.label} className="flex items-center gap-1.5 text-xs text-admin-textMuted">
        <span
          aria-hidden
          className={shape === "line" ? "h-0.5 w-3.5 rounded-full" : "h-2.5 w-2.5 rounded-[3px]"}
          style={{ background: it.color }}
        />
        {it.label}
      </li>
    ))}
  </ul>
);

// ─── Tooltip ─────────────────────────────────────────────────────────────────
function Tooltip({ x, y, width, title, rows }) {
  if (x == null) return null;
  const flip = x > width - 180;
  return (
    <div
      role="status"
      className="pointer-events-none absolute z-10 min-w-[150px] rounded-lg border border-admin-border bg-admin-surface px-3 py-2 shadow-soft-lg"
      style={{ left: flip ? x - 12 : x + 12, top: Math.max(0, y), transform: flip ? "translateX(-100%)" : undefined }}
    >
      <p className="mb-1 text-[11px] text-admin-textMuted">{title}</p>
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-admin-textMuted">
              <span aria-hidden className="h-0.5 w-3 rounded-full" style={{ background: r.color }} />
              {r.label}
            </span>
            <span className="font-semibold tabular-nums text-admin-text">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── ChartCard (khung + nút xem dạng bảng) ───────────────────────────────────
export function ChartCard({ title, subtitle, action, legend, table, className, children, icon: Icon, tone = "blue" }) {
  const [showTable, setShowTable] = useState(false);
  return (
    <section className={cx("flex flex-col rounded-2xl border border-admin-border bg-admin-surface shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_16px_-6px_rgba(15,23,42,0.08)]", className)}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5">
        <div className="flex min-w-0 items-start gap-3">
          {Icon ? (
            <span className={cx("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", (TONES[tone] || TONES.blue).chip)}>
              <Icon size={17} />
            </span>
          ) : null}
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-admin-text">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-admin-textMuted">{subtitle}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {action}
          {table ? (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              aria-pressed={showTable}
              title={showTable ? "Xem biểu đồ" : "Xem dạng bảng"}
              aria-label={showTable ? "Xem biểu đồ" : "Xem dạng bảng"}
              className={cx(
                "flex h-8 w-8 items-center justify-center rounded-lg transition",
                showTable ? "bg-admin-primaryLight text-admin-primary" : "text-admin-textMuted hover:bg-admin-muted hover:text-admin-text"
              )}
            >
              <Table2 size={15} />
            </button>
          ) : null}
        </div>
      </div>
      {legend && !showTable ? <div className="px-5 pt-3">{legend}</div> : null}
      <div className="flex-1 px-5 pb-5 pt-4">
        {showTable && table ? <DataTable {...table} /> : children}
      </div>
    </section>
  );
}

function DataTable({ columns, rows }) {
  return (
    <div className="max-h-[320px] overflow-auto rounded-lg border border-admin-border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-admin-muted">
          <tr>
            {columns.map((c, i) => (
              <th key={c} className={cx("px-3 py-2 font-semibold text-admin-textMuted", i === 0 ? "text-left" : "text-right")}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-admin-border">
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((cell, i) => (
                <td key={i} className={cx("px-3 py-1.5 text-admin-text", i === 0 ? "text-left" : "text-right tabular-nums")}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sparkline ───────────────────────────────────────────────────────────────
export function Sparkline({ values, color = VIZ[0], height = 36 }) {
  const [ref, width] = useWidth();
  const pts = useMemo(() => {
    const vals = (values || []).filter((v) => v != null);
    if (!width || !vals.length) return null;
    const max = Math.max(...vals, 1);
    const pad = 4;
    const step = vals.length > 1 ? (width - pad * 2) / (vals.length - 1) : 0;
    return vals.map((v, i) => [pad + i * step, pad + (height - pad * 2) * (1 - v / max)]);
  }, [values, width, height]);

  return (
    <div ref={ref} className="w-full" style={{ height }} aria-hidden>
      {pts ? (
        <svg width={width} height={height} className="block overflow-visible">
          <path
            d={`M${pts[0][0]},${height} L${pts.map((p) => p.join(",")).join(" L")} L${pts[pts.length - 1][0]},${height} Z`}
            fill={color}
            opacity={0.1}
          />
          <polyline
            points={pts.map((p) => p.join(",")).join(" ")}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r={3.5} fill={color} stroke="white" strokeWidth={2} />
        </svg>
      ) : null}
    </div>
  );
}

// ─── KPI tile: giá trị + delta so với kỳ trước + sparkline ───────────────────
export function KpiCard({ label, value, delta, deltaLabel, hint, trend, tone = "blue", icon: Icon }) {
  const t = TONES[tone] || TONES.blue;
  const d = Number(delta);
  const hasDelta = delta != null && Number.isFinite(d);
  const up = d > 0.5;
  const down = d < -0.5;
  const DeltaIcon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <div className="relative flex flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface p-4 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_16px_-6px_rgba(15,23,42,0.08)]">
      <span className={cx("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", t.bar)} aria-hidden />
      <span className={cx("pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br to-transparent opacity-80", t.glow)} aria-hidden />
      <div className="relative flex items-center justify-between gap-2">
        <p className="truncate text-xs font-semibold text-admin-textMuted">{label}</p>
        {Icon ? (
          <span className={cx("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", t.chip)}>
            <Icon size={17} />
          </span>
        ) : null}
      </div>
      <div className="relative mt-1 flex items-end justify-between gap-3">
        <p className="text-[28px] font-bold leading-none tracking-tight text-admin-text">{value}</p>
        {hasDelta ? (
          <span
            className={cx(
              "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
              up ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : down ? "bg-rose-50 text-rose-700 ring-rose-200" : "bg-admin-muted text-admin-textMuted ring-admin-border"
            )}
            title={deltaLabel}
          >
            <DeltaIcon size={12} aria-hidden />
            {`${d > 0 ? "+" : ""}${Math.round(d)}%`}
          </span>
        ) : null}
      </div>
      <p className="relative mt-1.5 truncate text-xs text-admin-textMuted">{hint || deltaLabel}</p>
      {trend?.length ? (
        <div className="relative mt-3">
          <Sparkline values={trend} color={t.hex} />
        </div>
      ) : null}
    </div>
  );
}

// ─── LineChart: nhiều series cùng đơn vị, crosshair + tooltip ────────────────
// Khi có quá nhiều điểm (mỗi điểm cần ≥ minPointWidth px), phần biểu đồ cuộn ngang được;
// trục Y đứng yên bên trái và mặc định cuộn tới cuối (dữ liệu mới nhất).
const AXIS_W = 44;

export function LineChart({ labels, series, height = 260, formatLabel = (l) => l, formatTick, area, minPointWidth = 14 }) {
  const [ref, cw] = useWidth();
  const scrollRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const pad = { top: 8, bottom: 26, x: 14 };
  const n = labels.length;
  const showArea = area ?? series.length === 1;
  const visibleW = Math.max(0, cw - AXIS_W);
  const plotW = Math.max(visibleW, n * minPointWidth);
  const scrollable = plotW > visibleW + 1;

  const geo = useMemo(() => {
    if (!cw || !n) return null;
    const max = Math.max(...series.flatMap((s) => s.values.filter((v) => v != null)), 1);
    const { top, ticks } = niceScale(max);
    const iw = plotW - pad.x * 2;
    const ih = height - pad.top - pad.bottom;
    const x = (i) => pad.x + (n > 1 ? (i / (n - 1)) * iw : iw / 2);
    const y = (v) => pad.top + ih * (1 - v / top);
    return { ticks, x, y, iw };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cw, plotW, height, n, series]);

  // Mặc định hiển thị phần mới nhất
  useEffect(() => {
    const el = scrollRef.current;
    if (el && scrollable) {
      el.scrollLeft = el.scrollWidth;
      setScrollLeft(el.scrollLeft);
    }
  }, [scrollable, plotW, n]);

  const xTickIdx = useMemo(() => {
    if (!n) return [];
    const want = Math.max(2, Math.floor(plotW / 84));
    const step = Math.max(1, Math.round((n - 1) / Math.max(1, want - 1)));
    const out = [];
    for (let i = 0; i < n; i += step) out.push(i);
    if (out[out.length - 1] !== n - 1) {
      if (n - 1 - out[out.length - 1] < step / 2) out.pop();
      out.push(n - 1);
    }
    return out;
  }, [n, plotW]);

  const pick = (e) => {
    if (!geo) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - pad.x;
    const i = Math.round((px / geo.iw) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };

  const onKey = (e) => {
    if (e.key === "ArrowRight") setHover((h) => Math.min(n - 1, (h ?? -1) + 1));
    else if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? n) - 1));
    else return;
    e.preventDefault();
  };

  // Phím mũi tên: giữ điểm đang chọn trong vùng nhìn thấy
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !geo || hover == null || !scrollable) return;
    const x = geo.x(hover);
    if (x < el.scrollLeft + 20) el.scrollLeft = x - 40;
    else if (x > el.scrollLeft + el.clientWidth - 20) el.scrollLeft = x - el.clientWidth + 40;
  }, [hover, geo, scrollable]);

  const tick = formatTick || fmtCompact;

  return (
    <div
      ref={ref}
      className="relative w-full outline-none"
      tabIndex={0}
      role="img"
      aria-label={`Biểu đồ đường: ${series.map((s) => s.label).join(", ")}. Dùng phím mũi tên để xem từng điểm${scrollable ? ", kéo ngang để xem thêm" : ""}.`}
      onKeyDown={onKey}
      onBlur={() => setHover(null)}
    >
      {geo ? (
        <div className="flex">
          {/* Trục Y cố định */}
          <svg width={AXIS_W} height={height} className="shrink-0" aria-hidden>
            {geo.ticks.map((t) => (
              <text key={t} x={AXIS_W - 8} y={geo.y(t)} dy="0.32em" textAnchor="end" fontSize="11" fill="var(--viz-ink-muted)" className="tabular-nums">
                {tick(t)}
              </text>
            ))}
          </svg>

          <div
            ref={scrollRef}
            onScroll={(e) => setScrollLeft(e.currentTarget.scrollLeft)}
            className={cx("min-w-0 flex-1", scrollable && "overflow-x-auto overflow-y-hidden pb-1 [scrollbar-width:thin]")}
          >
            <div className="relative" style={{ width: plotW, height }} onPointerMove={pick} onPointerLeave={() => setHover(null)}>
              <svg width={plotW} height={height} className="block">
                {geo.ticks.map((t) => (
                  <line key={t} x1={0} x2={plotW} y1={geo.y(t)} y2={geo.y(t)} stroke={t === 0 ? "var(--viz-axis)" : "var(--viz-grid)"} />
                ))}
                {xTickIdx.map((i) => (
                  <text
                    key={i}
                    x={geo.x(i)}
                    y={height - 6}
                    textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
                    fontSize="11"
                    fill="var(--viz-ink-muted)"
                  >
                    {formatLabel(labels[i])}
                  </text>
                ))}
                {series.map((s, si) => {
                  const color = s.color || VIZ[si];
                  // null = chưa có dữ liệu → ngắt đường thành nhiều đoạn, không vẽ như số 0
                  const segments = [];
                  let cur = [];
                  s.values.forEach((v, i) => {
                    if (v == null) {
                      if (cur.length) segments.push(cur);
                      cur = [];
                    } else cur.push(i);
                  });
                  if (cur.length) segments.push(cur);
                  return (
                    <g key={s.label}>
                      {segments.map((seg) => {
                        const pts = seg.map((i) => `${geo.x(i)},${geo.y(s.values[i])}`);
                        return (
                          <g key={seg[0]}>
                            {showArea ? (
                              <path
                                d={`M${geo.x(seg[0])},${geo.y(0)} L${pts.join(" L")} L${geo.x(seg[seg.length - 1])},${geo.y(0)} Z`}
                                fill={color}
                                opacity={0.1}
                              />
                            ) : null}
                            {seg.length === 1 ? (
                              <circle cx={geo.x(seg[0])} cy={geo.y(s.values[seg[0]])} r={3} fill={color} />
                            ) : (
                              <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                            )}
                          </g>
                        );
                      })}
                    </g>
                  );
                })}
                {hover != null ? (
                  <g>
                    <line x1={geo.x(hover)} x2={geo.x(hover)} y1={pad.top} y2={height - pad.bottom} stroke="var(--viz-axis)" />
                    {series.map((s, si) =>
                      s.values[hover] == null ? null : (
                        <circle key={s.label} cx={geo.x(hover)} cy={geo.y(s.values[hover])} r={4.5} fill={s.color || VIZ[si]} stroke="white" strokeWidth={2} />
                      )
                    )}
                  </g>
                ) : null}
              </svg>
              {hover != null ? (
                <Tooltip
                  x={geo.x(hover)}
                  y={pad.top}
                  width={scrollLeft + visibleW}
                  title={formatLabel(labels[hover], true)}
                  rows={series.map((s, si) => ({
                    label: s.label,
                    color: s.color || VIZ[si],
                    value: s.values[hover] == null ? "Chưa có dữ liệu" : `${fmtNum(s.values[hover])}${s.flags?.[hover] ? ` (${s.flags[hover]})` : ""}`,
                  }))}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ height }} />
      )}
      {scrollable ? (
        <p className="mt-1 text-right text-[11px] text-admin-textMuted">← Kéo hoặc cuộn ngang để xem thêm · {n} điểm</p>
      ) : null}
    </div>
  );
}

// ─── ColumnChart: cột nhóm theo hạng mục ─────────────────────────────────────
export function ColumnChart({ labels, series, height = 240, formatLabel = (l) => l }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { top: 8, right: 4, bottom: 26, left: 32 };
  const n = labels.length;

  const geo = useMemo(() => {
    if (!width || !n) return null;
    const max = Math.max(...series.flatMap((s) => s.values.filter((v) => v != null)), 1);
    const { top, ticks } = niceScale(max);
    const iw = width - pad.left - pad.right;
    const ih = height - pad.top - pad.bottom;
    const band = iw / n;
    const k = series.length;
    const gap = 2;
    const barW = Math.max(3, Math.min(24, (band * 0.72 - gap * (k - 1)) / k));
    const groupW = barW * k + gap * (k - 1);
    const y = (v) => pad.top + ih * (1 - v / top);
    return { ticks, band, barW, groupW, y, ih, gap };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, n, series]);

  // Bo 4px đầu cột, đáy vuông nằm trên baseline
  const barPath = (x, yTop, w, base) => {
    const h = base - yTop;
    if (h <= 0) return "";
    const r = Math.min(4, w / 2, h);
    return `M${x},${base} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + w - r} Q${x + w},${yTop} ${x + w},${yTop + r} V${base} Z`;
  };

  const showEvery = geo ? Math.max(1, Math.ceil(44 / geo.band)) : 1;

  return (
    <div
      ref={ref}
      className="relative w-full outline-none"
      style={{ height }}
      tabIndex={0}
      role="img"
      aria-label={`Biểu đồ cột: ${series.map((s) => s.label).join(", ")}. Dùng phím mũi tên để xem từng cột.`}
      onPointerLeave={() => setHover(null)}
      onBlur={() => setHover(null)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") setHover((h) => Math.min(n - 1, (h ?? -1) + 1));
        else if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? n) - 1));
        else return;
        e.preventDefault();
      }}
    >
      {geo ? (
        <svg width={width} height={height} className="block">
          {geo.ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={geo.y(t)} y2={geo.y(t)} stroke={t === 0 ? "var(--viz-axis)" : "var(--viz-grid)"} />
              <text x={pad.left - 8} y={geo.y(t)} dy="0.32em" textAnchor="end" fontSize="11" fill="var(--viz-ink-muted)">
                {fmtCompact(t)}
              </text>
            </g>
          ))}
          {labels.map((l, i) => {
            const bx = pad.left + i * geo.band;
            const gx = bx + (geo.band - geo.groupW) / 2;
            const base = geo.y(0);
            return (
              <g key={l} onPointerEnter={() => setHover(i)}>
                <rect x={bx} y={pad.top} width={geo.band} height={geo.ih} fill={hover === i ? "rgb(var(--admin-muted))" : "transparent"} />
                {series.map((s, si) => (
                  <path
                    key={s.label}
                    d={barPath(gx + si * (geo.barW + geo.gap), geo.y(s.values[i]), geo.barW, base)}
                    fill={s.color || VIZ[si]}
                  />
                ))}
                {i % showEvery === 0 || i === n - 1 ? (
                  <text x={bx + geo.band / 2} y={height - 6} textAnchor="middle" fontSize="11" fill="var(--viz-ink-muted)">
                    {formatLabel(l)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      ) : null}
      {geo && hover != null ? (
        <Tooltip
          x={pad.left + hover * geo.band + geo.band / 2}
          y={pad.top}
          width={width}
          title={formatLabel(labels[hover], true)}
          rows={series.map((s, si) => ({ label: s.label, color: s.color || VIZ[si], value: fmtNum(s.values[hover]) }))}
        />
      ) : null}
    </div>
  );
}

// ─── BarList: xếp hạng ngang (một màu, giá trị ở đầu thanh) ──────────────────
export function BarList({ items, color = VIZ[0], empty = "Chưa có dữ liệu", formatValue = fmtNum }) {
  if (!items?.length) return <p className="py-6 text-center text-sm text-admin-textMuted">{empty}</p>;
  const max = Math.max(...items.map((x) => x.value), 1);
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.key ?? it.label} title={`${it.label}: ${formatValue(it.value)}`}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-admin-text">{it.label}</span>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-admin-text">
              {formatValue(it.value)}
              {it.suffix ? <span className="ml-1 font-normal text-admin-textMuted">{it.suffix}</span> : null}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-admin-muted">
            <div
              className="h-full rounded-full transition-[width] duration-700"
              style={{ width: `${Math.max(2, (it.value / max) * 100)}%`, background: color }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Meter: tỷ lệ trên tổng, track cùng tông ─────────────────────────────────
export function Meter({ label, value, total, color = VIZ[0] }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-sm text-admin-text">{label}</span>
        <span className="text-xs text-admin-textMuted">
          <span className="font-semibold text-admin-text">{fmtNum(value)}</span> / {fmtNum(total)} · {pct}%
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full"
        style={{ background: `color-mix(in srgb, ${color} 16%, transparent)` }}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={value}
        aria-label={label}
      >
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── StackedBar 100%: tỷ trọng các phần ──────────────────────────────────────
export function StackedBar({ parts }) {
  const total = parts.reduce((a, p) => a + p.value, 0);
  return (
    <div>
      <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full" role="img" aria-label={parts.map((p) => `${p.label} ${p.value}`).join(", ")}>
        {total > 0
          ? parts.map((p, i) =>
              p.value > 0 ? (
                <div
                  key={p.label}
                  title={`${p.label}: ${fmtNum(p.value)}`}
                  className={cx("h-full", i === 0 && "rounded-l-full", i === parts.length - 1 && "rounded-r-full")}
                  style={{ width: `${(p.value / total) * 100}%`, background: p.color || VIZ[i] }}
                />
              ) : null
            )
          : <div className="h-full w-full bg-admin-muted" />}
      </div>
      <ul className="mt-3 space-y-1.5">
        {parts.map((p, i) => (
          <li key={p.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 text-admin-text">
              <span aria-hidden className="h-2.5 w-2.5 rounded-[3px]" style={{ background: p.color || VIZ[i] }} />
              {p.label}
            </span>
            <span className="text-xs text-admin-textMuted">
              <span className="font-semibold text-admin-text">{fmtNum(p.value)}</span>
              {total > 0 ? ` · ${Math.round((p.value / total) * 100)}%` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
